import Utils from '@/mixins/utils'

const { formatTranslationKeys } = Utils.methods
const DEFAULT_MODEL = 'gemini' // 'openai' // 'gemini'
const DEFAULT_MODEL_VERSION = 'gemini-2.0-flash-lite' // 'gpt-4.1-nano' // 'gpt-4o-mini' // 'gpt-3.5-turbo' // 'gemini-2.5-flash'
const fallbackModels = [
	{
		model: 'gemini',
		modelVersion: 'gemini-2.0-flash-lite'
	},
	{
		model: 'openai',
		modelVersion: 'gpt-4.1-nano'
	}
]

const state = {
	// Loading states
	isTranslating: false,
	loadingElements: new Set(), // Track which elements are currently being translated

	// // Translation cache to avoid duplicate API calls
	// translationCache: new Map(),

	// Configuration
	config: {
		apiEndpoint: '/auth/users', // This should be configured based on your API
		maxCacheSize: 1000,
		cacheExpiry: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
		// supportedLanguages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'zh', 'ja', 'ko'],
		// defaultSourceLanguage: 'en'
	},

	// Error handling
	lastError: null,
	errorCount: 0
}

const mutations = {
	SET_TRANSLATING_STATE (state, { elementId, isTranslating }) {
		if (isTranslating) {
			state.loadingElements.add(elementId)
		} else {
			state.loadingElements.delete(elementId)
		}
		state.isTranslating = state.loadingElements.size > 0
	},

	// SET_TRANSLATION_CACHE (state, { cacheKey, translation, timestamp }) {
	// 	// Implement cache size limit
	// 	if (state.translationCache.size >= state.config.maxCacheSize) {
	// 		// Remove oldest entries
	// 		const entries = Array.from(state.translationCache.entries())
	// 		entries.sort((a, b) => a[1].timestamp - b[1].timestamp)
	// 		const toRemove = entries.slice(0, Math.floor(state.config.maxCacheSize * 0.1))
	// 		toRemove.forEach(([key]) => state.translationCache.delete(key))
	// 	}

	// 	state.translationCache.set(cacheKey, {
	// 		translation,
	// 		timestamp: timestamp || Date.now()
	// 	})
	// },

	// CLEAR_TRANSLATION_CACHE (state) {
	// 	state.translationCache.clear()
	// },

	SET_ERROR (state, error) {
		state.lastError = error
		state.errorCount++
	},

	CLEAR_ERROR (state) {
		state.lastError = null
	},

	UPDATE_CONFIG (state, config) {
		state.config = { ...state.config, ...config }
	}
}

const actions = {
	async translateText ({ commit, dispatch, state }, {
		text,
		sourceLanguage = 'English',
		targetLanguage,
		targetLanguageCode,
		cacheIndex = 'translationCaching',
		cacheKey = '',
		stream = 0,
		model = DEFAULT_MODEL,
		modelVersion = DEFAULT_MODEL_VERSION,
		forceRefresh = 0,
		elementId = null,
		context = null
	}) {
		if (!text || !text.trim()) {
			throw new Error('No text provided for translation')
		}

		if (!targetLanguage) {
			throw new Error('Target language is required')
		}

		// Generate cache key
		if (!cacheKey) {
			cacheKey = `${formatTranslationKeys(text)}|${sourceLanguage || 'auto'}|${targetLanguage}${context ? `|${context}` : ''}`
		}

		// // Check cache first
		// const cached = state.translationCache.get(cacheKey)
		// if (cached && (Date.now() - cached.timestamp) < state.config.cacheExpiry) {
		// 	return cached.translation
		// }

		// Set loading state
		if (elementId) {
			commit('SET_TRANSLATING_STATE', { elementId, isTranslating: true })
		}

		try {
			commit('CLEAR_ERROR')

			const response = await dispatch('callApi', {
				method: 'POST',
				url: state.config.apiEndpoint,
				data: {
					cache: forceRefresh ? 0 : 1,
					cache_index: cacheIndex,
					cache_key: cacheKey,
					method: 'chat',
					stream,
					model,
					version: modelVersion,
					// type: 'text', // 'json',
					message: `You are a translator, translating into '${targetLanguage}' (${targetLanguageCode}), this phrase: '${text}'. Respond with the translated phrase only.`
					// source_language: sourceLanguage || state.config.defaultSourceLanguage,
					// target_language: targetLanguage,
					// context: context
				}
			}, { root: true })

			if (response.status >= 400) {
				const error = new Error(`Translation API request failed with status ${response.status}`)
				error.response = response
				throw error
			}

			let translatedText
			if (stream != 1) {
				translatedText = response.data.response || response.data.translation || response.data.result
			} else {
				// streamed response with Event Data
				const data = response.data
				const chunks = data.split('\n')
				translatedText = []

				for (const chunk of chunks) {
					if (chunk.startsWith('data: ')) {
						const text = chunk.substring(5).trim()
						if (text !== '[DONE]') {
							translatedText.push(text)
						}
					}
				}

				// Join the translated text chunks into a single string
				translatedText = translatedText.join(' ')
			}

			if (!translatedText) {
				commit('Toast/_addError', `Failed translating at this moment`, { root: true })
				// throw new Error('Invalid response from translation API')
			}

			// // Cache the result
			// commit('SET_TRANSLATION_CACHE', {
			// 	cacheKey,
			// 	translation: translatedText,
			// 	timestamp: Date.now()
			// })

			// // Show success notification
			// dispatch('Toast/_add', 'Translation completed successfully', { root: true })

			return translatedText
		} catch (error) {
			// check for model overloaded err
			// detail: "The model is overloaded. Please try again later."
			// status: 417
			if (error.response?.data?.detail?.toLowerCase().includes('overloaded')) {
				if (fallbackModels.length) {
					const { model, modelVersion } = fallbackModels.shift()
					return dispatch('translateText', {
						text,
						sourceLanguage,
						targetLanguage,
						targetLanguageCode,
						cacheIndex,
						cacheKey,
						stream,
						model,
						modelVersion,
						forceRefresh,
						elementId,
						context
					})
				} else {
					commit('Toast/_addError', `Cannot apply translation at this moment. Please try again later.`, { root: true })
				}
			} else {
				commit('Toast/_addError', `Translation failed: ${error.message}`, { root: true })
			}
			console.error('AI Translation Error:', error)

			const errorMessage = error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				'Translation failed. Please try again.'

			commit('SET_ERROR', errorMessage)
			commit('Toast/_addError', `Translation failed: ${errorMessage}`, { root: true })

			throw new Error(errorMessage)
		} finally {
			// Clear loading state
			if (elementId) {
				commit('SET_TRANSLATING_STATE', { elementId, isTranslating: false })
			}
		}
	},

	async batchTranslate ({ dispatch }, { translations, targetLanguage, sourceLanguage = null }) {
		const results = []
		const errors = []

		for (const item of translations) {
			try {
				const result = await dispatch('translateText', {
					text: item.text,
					sourceLanguage,
					targetLanguage,
					elementId: item.elementId,
					context: item.context
				})
				results.push({ ...item, translatedText: result })
			} catch (error) {
				errors.push({ ...item, error: error.message })
			}
		}

		return { results, errors }
	},
	clearCache ({ commit }) {
		commit('CLEAR_TRANSLATION_CACHE')
		commit('CLEAR_ERROR')
	},
	updateConfig ({ commit }, config) {
		commit('UPDATE_CONFIG', config)
	},
	isElementTranslating ({ state }, elementId) {
		return state.loadingElements.has(elementId)
	}
}

const getters = {
	isTranslating: (state) => state.isTranslating,

	isElementTranslating: (state) => (elementId) => {
		return state.loadingElements.has(elementId)
	},

	// getCachedTranslation: (state) => (text, sourceLanguage, targetLanguage, context = null) => {
	// 	const cacheKey = `${text}|${sourceLanguage || 'auto'}|${targetLanguage}|${context || ''}`
	// 	const cached = state.translationCache.get(cacheKey)

	// 	if (cached && (Date.now() - cached.timestamp) < state.config.cacheExpiry) {
	// 		return cached.translation
	// 	}

	// 	return null
	// },
	getConfig: (state) => state.config,
	getLastError: (state) => state.lastError
	// getCacheSize: (state) => state.translationCache.size,
	// getSupportedLanguages: (state) => state.config.supportedLanguages
}

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters
}
